import { Dialog, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CURRENCY } from "../../../../constants/Variables";
import DialogHeader from "../../../../components/Dialog/Header";
import DialogBody from "../../../../components/Dialog/Body";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

export const PricingDialog = forwardRef(({}, ref) => {
  // Dialog open state
  const [open, setOpen] = useState(false);

  // Selected movement state
  const [selectedMovement, setSelectedMovement] = useState(null);

  // Translation state
  const { t } = useTranslation();

  // Get current language
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  // Open dialog using useImperativeHandle
  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setSelectedMovement(params?.data || null);
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  // Handle close dialog
  const handleClose = () => {
    setOpen(false);
    setSelectedMovement(null);
  };

  // Get product name
  const productName =
    selectedMovement?.productData?.name?.[currentLanguage] || "-";
  // Get product sku
  const productSku = selectedMovement?.sku || "-";

  // Get pricing
  const pricing = selectedMovement?.pricing || {};

  // Pricing rows
  const pricingRows = [
    {
      label: t("common.purchasePriceWithoutVat"),
      value: pricing?.price,
      currency: CURRENCY,
    },
    {
      label: t("common.discount"),
      value: pricing?.discount,
      currency: CURRENCY,
    },
    {
      label: t("common.profitMargin"),
      value: pricing?.profitMargin,
      currency: "%",
    },
    {
      label: t("common.finalPrice"),
      value: pricing?.finalPrice,
      currency: CURRENCY,
    },
    { label: t("common.vat"), value: pricing?.vat, currency: "%" },
    {
      label: t("common.vatAmount", { vat: pricing?.vat || 0 }),
      value: pricing?.vatAmount,
      currency: CURRENCY,
    },
  ];

  // Visible pricing rows
  const visiblePricingRows =
    selectedMovement?.reason === "stock_in"
      ? pricingRows.filter(
          (item) =>
            item.label !== t("common.vat") &&
            item.label !== t("common.vatAmount", { vat: pricing?.vat || 0 }),
        )
      : pricingRows;

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogHeader
        title={productName}
        secondaryHeading={`${t("common.sku")}: ${productSku}`}
        onClose={handleClose}
        icon={<ProductionQuantityLimitsIcon />}
      />
      <DialogBody>
        <Box
          border={"1px solid"}
          borderColor={"divider"}
          borderRadius={2}
          overflow={"hidden"}
          bgcolor={"background.paper"}
        >
          <Box
            px={2}
            py={1.5}
            bgcolor={"grey.50"}
            borderBottom={"1px solid"}
            borderColor={"divider"}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {t("common.pricingDetails")}
            </Typography>
          </Box>

          {visiblePricingRows.map((item, index) => (
            <Box
              key={item.label}
              px={2}
              py={1.5}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={2}
              borderBottom={
                index !== visiblePricingRows.length - 1 ? "1px solid" : "none"
              }
              borderColor={"divider"}
            >
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                textAlign={"right"}
                px={1.25}
                py={0.5}
                borderRadius={1}
                bgcolor={"grey.100"}
                minWidth={72}
              >
                {item.value ?? "-"}
                {item.currency && <span> {item.currency}</span>}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogBody>
    </BootstrapDialog>
  );
});
